import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    TemplateRef,
    ViewEncapsulation,
    inject,
    ChangeDetectorRef
} from "@angular/core";
import { NgTemplateOutlet } from "@angular/common";
import { Subject, Observable } from "rxjs";
import { takeUntil, map } from "rxjs/operators";
import { LgObserveSizeService } from "@logex/framework/ui-core";

import { LgSlideoutService } from "./lg-slideout.service";
import { LgSlideoutApi, LgSlideoutVariant, LgSlideoutState } from "./lg-slideout.types";
import { LG_FW_UI_STATE_SERVICE } from "../lg-fw-ui-state";
import { LgSlideoutHolderComponent } from "./lg-slideout-holder.component";
import { LgSlideoutHolderHeaderComponent } from "./lg-slideout-holder-header.component";

@Component({
    selector: "lg-slideout",
    standalone: true,
    templateUrl: "./lg-slideout.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: "lg-slideout"
    },
    imports: [NgTemplateOutlet, LgSlideoutHolderComponent, LgSlideoutHolderHeaderComponent],
    encapsulation: ViewEncapsulation.None
})
export class LgSlideoutComponent implements OnInit, OnDestroy {
    private _changeDetectorRef = inject(ChangeDetectorRef);
    private _element = inject(ElementRef);
    private _renderer = inject(Renderer2);
    private _service = inject(LgSlideoutService);
    private _sizeObserver = inject(LgObserveSizeService);
    private _uiState = inject(LG_FW_UI_STATE_SERVICE);

    /**
     * Slideout variant
     *
     * @type {"left" | "right"}
     */
    @Input() variant: LgSlideoutVariant = "right";
    @Input() width = 0;
    @Input() initializeAsPinned = false;

    /**
     * Slideout identifier
     */
    @Input({ required: true }) id!: string;

    /*
     * Can reduce boilerplate code so no TS logic is required for some scenarios:
     * <lg-slideout #myLgSlideout>...</lg-slideout>
     * <span *ngIf="myLgSlideout.pinned$ | async"></span>
     */
    pinned$!: Observable<boolean>;

    slideoutApi!: LgSlideoutApi;

    /**
     * Emits on pin toggle
     */
    @Output() readonly pinToggled = new EventEmitter<boolean>(false);

    /**
     * Emits on Slideout show
     */
    @Output("shown") readonly shown = new EventEmitter<void>();

    /**
     * Emits on Slideout hide
     */
    @Output("hidden") readonly hidden = new EventEmitter<void>();

    _pinned = false;
    _hidePin = false;
    _expanded = false;
    _suppressBackdropClose = false;
    _panelWidth: number | undefined = undefined;
    _template?: TemplateRef<object> | undefined;
    _templateContext: object = {};

    _overlayExpanded = false;
    _overlayHidePin = false;
    _overlaySuppressBackdropClose = false;
    _overlayPanelWidth: number | undefined = undefined;
    _overlayTemplate?: TemplateRef<object> | undefined;
    _overlayTemplateContext: object = {};
    _overlayApi!: LgSlideoutApi;

    private _destroyed$ = new Subject<void>();

    async ngOnInit(): Promise<void> {
        this.slideoutApi = this._service.api(this.id, this.variant);
        this._overlayApi = this._service.overlayApi(this.id, this.variant);

        this._overlayApi.state$
            .pipe(takeUntil(this._destroyed$))
            .subscribe(
                ({
                    expanded,
                    template,
                    templateContext,
                    hidePin,
                    panelWidth,
                    suppressBackdropClose
                }: LgSlideoutState) => {
                    this._overlayExpanded = expanded;
                    this._overlayHidePin = hidePin ?? false;
                    this._overlaySuppressBackdropClose = suppressBackdropClose ?? false;
                    this._overlayTemplate = template;
                    this._overlayTemplateContext = templateContext ?? {};
                    this._overlayPanelWidth = panelWidth;
                    this._changeDetectorRef.markForCheck();
                }
            );

        this.slideoutApi.state$
            .pipe(takeUntil(this._destroyed$))
            .subscribe(
                ({
                    pinned,
                    expanded,
                    template,
                    templateContext,
                    hidePin,
                    panelWidth,
                    suppressBackdropClose
                }: LgSlideoutState) => {
                    let needToSetElementWidth = false;

                    if (
                        (template != null && template !== this._template && pinned) ||
                        (this._pinned !== pinned && this._pinned != null)
                    ) {
                        this._uiState.setSidebarPinnedPanelId(
                            this.variant,
                            this._service.activePanelVariant ?? ""
                        );
                    }

                    if (this._pinned !== pinned) {
                        if (this._pinned != null) {
                            this._uiState.setSidebarPinned(this.variant, pinned);
                            this._sizeObserver.recalculate();
                        }
                        this._pinned = pinned;
                        this.pinToggled.emit(pinned);
                        needToSetElementWidth = true;
                    }
                    if (this._expanded !== expanded) {
                        this._expanded = expanded;
                        needToSetElementWidth = true;
                    }
                    if (this._panelWidth !== panelWidth) {
                        this._panelWidth = panelWidth;
                        needToSetElementWidth = true;
                    }

                    this._hidePin = hidePin ?? false;
                    this._suppressBackdropClose = suppressBackdropClose ?? false;
                    this._template = template;
                    this._templateContext = templateContext ?? {};

                    if (needToSetElementWidth) this._trySetElementWidth();
                    this._changeDetectorRef.markForCheck();
                }
            );

        this.pinned$ = this.slideoutApi.state$.pipe(map(state => state.pinned));

        let initAsPinned = !!this.initializeAsPinned;

        const uiStatePinned = await this._uiState.getSidebarPinned(this.variant);
        if (uiStatePinned != null) {
            initAsPinned = uiStatePinned;
        }
        this._service.setAvailablePanels([]);
        this._pinned = this._pinned || initAsPinned;
        this.slideoutApi.togglePinned(this._pinned);
        const templatePanelId = await this._uiState.getSidebarPinnedPanelId(this.variant);
        this.slideoutApi.setActivePanel(templatePanelId);
        this._service.availablePanelsState$.pipe(takeUntil(this._destroyed$)).subscribe(panels => {
            if (Object.keys(panels).length === 0) return;
            if (initAsPinned) {
                this.slideoutApi.toggleExpanded(true);
                this._trySetElementWidth();
                initAsPinned = false;
            }

            const panel = this._service.currentPanelVariant
                ? this._service.getAvailablePanel(this._service.currentPanelVariant)
                : this._service.getAvailablePanel(Object.keys(panels)[0]);

            if (
                panel != null &&
                Object.keys(panels).length > this._service.getAvailablePanels().length
            ) {
                this._service.setAvailablePanels(Object.keys(panels));
                this.slideoutApi.setActivePanel(panel.panelVariant);
            }
            this._changeDetectorRef.markForCheck();
        });
    }

    ngOnDestroy(): void {
        this.slideoutApi.slideoutHidden();
        this._overlayApi.slideoutHidden();
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    protected _hide(): void {
        this.slideoutApi.toggleExpanded(false);
    }

    protected _onBackdropClick(): void {
        if (this._overlayExpanded) {
            if (!this._overlaySuppressBackdropClose) {
                this._overlayApi.toggleExpanded(false);
            }
        } else if (!this._suppressBackdropClose) {
            this.slideoutApi.toggleExpanded(false);
        }
    }

    protected _togglePin(): void {
        this.slideoutApi.togglePinned();
        this._uiState.setSidebarPinned(this.variant, this._pinned);
        this._sizeObserver.recalculate();
    }

    protected _onShown(): void {
        this.slideoutApi.slideoutShown();
        this.shown.emit();
        this._changeDetectorRef.markForCheck();
    }

    protected _onHidden(): void {
        this.slideoutApi.slideoutHidden();
        this.hidden.emit();
        this._changeDetectorRef.markForCheck();
    }

    protected _onOverlayShown(): void {
        this._overlayApi.slideoutShown();
        this._changeDetectorRef.markForCheck();
    }

    protected _onOverlayHidden(): void {
        this._overlayApi.slideoutHidden();
        this._changeDetectorRef.markForCheck();
    }

    private _trySetElementWidth(): void {
        if (this._element) {
            const effectiveWidth = this._panelWidth ?? this.width;
            const newWidth = this._pinned && this._expanded ? effectiveWidth : 0;
            this._renderer.setStyle(this._element.nativeElement, "min-width", `${newWidth}px`);
        }
    }
}
